import { useState, useEffect } from "react";

// Reusable FAQ section with built-in Schema.org FAQPage JSON-LD for SEO.
// Pass eyebrow/title/items/testIdPrefix.
const FAQSection = ({
  eyebrow = "Common questions",
  title,
  intro,
  items,
  testIdPrefix = "faq",
  variant = "light", // 'light' | 'cream'
}) => {
  const [open, setOpen] = useState(0);
  const isCream = variant === "cream";

  // Inject FAQPage schema once on mount (and clean up on unmount)
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-faq-schema", testIdPrefix);
    tag.textContent = JSON.stringify(schema);
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      data-testid={`${testIdPrefix}-section`}
      className={`section ${isCream ? "bg-[var(--ss-cream)]" : ""}`}
    >
      <div className="container-px max-w-4xl mx-auto">
        <div className="text-center">
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          {title && (
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              {title}
            </h2>
          )}
          {intro && (
            <p className="mt-4 text-[var(--ss-ink-soft)] max-w-2xl mx-auto">
              {intro}
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                data-testid={`${testIdPrefix}-item-${i + 1}`}
                className={`rounded-2xl border transition-colors ${
                  isOpen
                    ? "border-[var(--ss-gold)] bg-white"
                    : "border-[var(--ss-line)] bg-white"
                }`}
              >
                <button
                  data-testid={`${testIdPrefix}-toggle-${i + 1}`}
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-5 py-4 md:px-7 md:py-5 flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <span className="font-serif text-lg md:text-xl text-[var(--ss-teal-800)] leading-snug">
                    {it.q}
                  </span>
                  <span
                    className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-transform ${
                      isOpen
                        ? "bg-[var(--ss-teal-700)] text-[var(--ss-cream)] rotate-45"
                        : "bg-[var(--ss-cream)] text-[var(--ss-teal-700)]"
                    }`}
                  >
                    <i className="fa-solid fa-plus text-sm"></i>
                  </span>
                </button>
                {isOpen && (
                  <div
                    data-testid={`${testIdPrefix}-answer-${i + 1}`}
                    className="px-5 pb-5 md:px-7 md:pb-6 text-[var(--ss-ink-soft)] leading-relaxed"
                  >
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
